/**
 * Layout
 * ======
 * App shell: sidebar on the left (logo + nav + status footer + user
 * card + theme toggle + sign out), main content fills the rest. Ported
 * from the v0 design while preserving every functional element the
 * pre-redesign sidebar surfaced (API health, LLM badge, Admin badge,
 * Diagnostics banner, user identity, sign-out flow).
 *
 * The Outlet is wrapped in <DiagnosticsBanner> so missing pipeline
 * deps are still surfaced site-wide; the banner takes care of its
 * own visibility (renders nothing when everything's green).
 */
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Upload as UploadIcon, GitBranch, LogOut, Database, Library,
  Dna, Sparkles, Shield, Cpu, FileText, Archive, Sigma, Microscope,
  Orbit, Crosshair,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getHealth, getRun, getUploadPreview } from "../api/client";
import { useAuth, useAdmin } from "../auth/AuthProvider";
import DiagnosticsBanner from "./DiagnosticsBanner";
import ErrorBoundary from "./ErrorBoundary";
import ThemeToggle from "./ThemeToggle";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/uploads",        label: "Uploads",        icon: UploadIcon },
  { to: "/runs",           label: "Runs",           icon: GitBranch },
  { to: "/catalog",        label: "Catalog",        icon: Library },
  // Atlas overview: "data is the hero" view of the catalog (aggregate
  // stats + disease distribution + cohort cards). The demo landing for
  // the curated cohort asset.
  { to: "/atlas",          label: "Atlas",          icon: Microscope },
  // Cross-dataset analysis (Phase 6.1): concat-then-DE across 2+ catalog
  // datasets. HIDDEN from the nav (2026-07-13 demo-focus): its own UI labels
  // it "exploratory only / pseudoreplicated / p-values not calibrated" and
  // steers users to Meta-analysis, so surfacing it is a statistical liability
  // in a demo. The /cross-analysis route still exists (reachable by URL) --
  // restore this entry when the exploratory volcano is genuinely wanted.
  // { to: "/cross-analysis", label: "Cross-dataset",  icon: Layers },
  // Cross-cohort meta-analysis (Phase 5.3): pseudobulk-per-donor +
  // random-effects pooling -> forest plot. Donor is the replication unit
  // (vs Cross-dataset's per-cell Wilcoxon), so this is the publication-
  // grade view for "is the effect consistent across cohorts?".
  { to: "/meta-analysis",  label: "Meta-analysis",  icon: Sigma },
  // Donor map (Phase 5.3): donor-level pseudobulk embedding rendered as an
  // interactive 3D scatter coloured by disease. Sits next to Meta-analysis
  // -- same "donor is the unit" framing, just a visual instead of a forest.
  { to: "/donor-map",      label: "Donor map",      icon: Orbit },
  // Reference mapping (Phase 5.1): position the user's OWN finished run
  // against the catalog's healthy<->disease axis. The user-facing payoff
  // of the curated cohort asset -- "where does my sample sit?".
  { to: "/reference-mapping", label: "Position sample", icon: Crosshair },
  // System info: package versions + annotation models. Visible to
  // every signed-in user (info is public + needed for methods sections).
  { to: "/system",         label: "System",         icon: Cpu },
];

// Admin-only nav. Rendered as a separate section below the main nav
// with a small "ADMIN" label so it visually doesn't compete with the
// user's daily-driver routes. Shield icon mirrors the existing admin
// badge styling.
//
// Registry moved here from the main NAV: when catalog datasets are
// single-sample (1:1:1 donor/sample/dataset), the Registry's separate
// donor / sample / cell pages don't add information that's not already
// on the Catalog detail panel. Keeping the endpoint accessible for
// admin debugging (and for future multi-sample cohorts) but out of
// the everyday user's sidebar.
const ADMIN_NAV = [
  { to: "/admin/catalog/uploads", label: "Catalog Uploads", icon: UploadIcon },
  { to: "/admin/catalog/runs",    label: "Catalog Runs",    icon: GitBranch },
  { to: "/admin/catalog/trash",   label: "Catalog Trash",   icon: Archive },
  { to: "/registry",              label: "Registry",        icon: Database },
  { to: "/admin/audit",           label: "Audit log",       icon: FileText },
];

export default function Layout() {
  const [health, setHealth] = useState<string>("unknown");
  const [llmReady, setLlmReady] = useState(false);
  const { user, signOut } = useAuth();
  const isAdmin = useAdmin();
  const nav = useNavigate();
  const loc = useLocation();

  // /runs/<id> shares one URL pattern for personal AND catalog reference
  // runs -- the route component just reads run.is_catalog_reference to
  // adjust its UI. The sidebar can't infer mode from URL alone, so when
  // we're on a /runs/<id> path we peek the run row to decide which nav
  // entry should glow. Cached via react-query so we don't double-fetch
  // (RunDetailPage uses the same ["run", id] key).
  const runIdMatch = loc.pathname.match(/^\/runs\/([^/]+)$/);
  const inspectedRunId = runIdMatch ? runIdMatch[1] : null;
  const inspectedRunQ = useQuery({
    queryKey: ["run", inspectedRunId],
    queryFn: () => getRun(inspectedRunId!),
    enabled: !!inspectedRunId,
    staleTime: 30_000,
  });
  // Same logic for /uploads/<id>/review: shared URL pattern for both
  // personal upload review AND catalog upload review. Peek the preview
  // (which carries as_catalog) to flip the highlight to "Catalog
  // Uploads" when reviewing a catalog upload. Same react-query key
  // as UploadReviewPage so this is a free fetch.
  const reviewIdMatch = loc.pathname.match(/^\/uploads\/([^/]+)\/review$/);
  const inspectedUploadId = reviewIdMatch ? reviewIdMatch[1] : null;
  const inspectedPreviewQ = useQuery({
    queryKey: ["upload-preview", inspectedUploadId],
    queryFn: () => getUploadPreview(inspectedUploadId!),
    enabled: !!inspectedUploadId,
    staleTime: 30_000,
  });
  const viewingCatalogUploadReview = !!(
    inspectedUploadId && inspectedPreviewQ.data?.as_catalog
  );

  // True when the URL is /runs/<id> AND that run is a catalog reference.
  // Used to flip sidebar highlight: a catalog reference run should
  // light up "Catalog Runs", not "Runs".
  const viewingCatalogRef = !!(
    inspectedRunId && inspectedRunQ.data?.is_catalog_reference
  );

  useEffect(() => {
    getHealth()
      .then((h) => {
        setHealth(h.status);
        setLlmReady(h.anthropic_configured);
      })
      .catch(() => setHealth("offline"));
  }, []);

  const handleSignOut = async () => {
    await signOut();
    nav("/login", { replace: true });
  };

  // Short label: email local-part falls back to a placeholder if the
  // identity provider didn't give us an email (e.g. anonymous sign-in).
  const userLabel = user?.email ? user.email.split("@")[0] : "Signed in";
  // Two letters from the label for the avatar chip (e.g. "ab26324730" -> "AB").
  const initials = (userLabel.slice(0, 2)).toUpperCase();

  return (
    <div className="h-full flex bg-background text-foreground">
      <aside className="w-64 h-full flex flex-col border-r border-sidebar-border bg-sidebar">
        {/* Brand */}
        <div className="p-6 border-b border-sidebar-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent
                            flex items-center justify-center glow-primary">
              <Dna className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-semibold tracking-tight">PBMC Platform</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                scRNA-seq + AI
              </p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              // Override the URL-based isActive for shared URL patterns
              // where the same route component serves both a personal
              // and a catalog flavour:
              //   - /runs/<id>           -- might be a catalog reference
              //   - /uploads/<id>/review -- might be a catalog upload
              // In both cases we want the corresponding ADMIN_NAV entry
              // (Catalog Runs / Catalog Uploads) to light up instead of
              // this personal entry. Flip isActive off here.
              className={({ isActive }) => {
                const reallyActive =
                  (n.to === "/runs" && viewingCatalogRef) ||
                  (n.to === "/uploads" && viewingCatalogUploadReview)
                    ? false
                    : isActive;
                return cn(
                  "w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium",
                  "transition-all duration-200",
                  reallyActive
                    ? "bg-primary/10 text-primary border border-primary/20 glow-accent"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted border border-transparent"
                );
              }}
            >
              {({ isActive }) => {
                const reallyActive =
                  (n.to === "/runs" && viewingCatalogRef) ||
                  (n.to === "/uploads" && viewingCatalogUploadReview)
                    ? false
                    : isActive;
                return (
                  <>
                    <n.icon className={cn("w-4 h-4", reallyActive && "text-primary")} />
                    {n.label}
                    {reallyActive && (
                      <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />
                    )}
                  </>
                );
              }}
            </NavLink>
          ))}

          {/* Admin Catalog workspace -- only shown to admins. The
              small uppercase label visually breaks it out from the
              user's daily-driver routes so admin work doesn't get
              mixed in. */}
          {isAdmin && (
            <div className="pt-4">
              <div className="px-4 pb-2 text-[10px] font-semibold uppercase
                              tracking-wider text-muted-foreground flex
                              items-center gap-1.5">
                <Shield className="w-3 h-3" />
                Admin
              </div>
              {ADMIN_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  // Same override logic in reverse: when we're on a
                  // shared URL pattern that turned out to be the catalog
                  // flavour, light up the corresponding admin entry
                  // here even though the URL doesn't start with
                  // /admin/catalog/...
                  className={({ isActive }) => {
                    const reallyActive =
                      (n.to === "/admin/catalog/runs" && viewingCatalogRef) ||
                      (n.to === "/admin/catalog/uploads" && viewingCatalogUploadReview)
                        ? true
                        : isActive;
                    return cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium",
                      "transition-all duration-200",
                      reallyActive
                        ? "bg-warning/10 text-warning border border-warning/20"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted border border-transparent"
                    );
                  }}
                >
                  {({ isActive }) => {
                    const reallyActive =
                      (n.to === "/admin/catalog/runs" && viewingCatalogRef) ||
                      (n.to === "/admin/catalog/uploads" && viewingCatalogUploadReview)
                        ? true
                        : isActive;
                    return (
                      <>
                        <n.icon className={cn("w-4 h-4", reallyActive && "text-warning")} />
                        {n.label}
                        {reallyActive && (
                          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-warning" />
                        )}
                      </>
                    );
                  }}
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-sidebar-border space-y-3">
          {/* Status indicators: API health + LLM availability.
              Admin badge slides in next to them when the current user
              has admin privileges, mirroring the trust signal the old
              sidebar surfaced. */}
          <div className="flex items-center justify-between px-2 text-xs">
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-2 h-2 rounded-full",
                health === "ok"
                  ? "bg-success animate-pulse"
                  : "bg-destructive",
              )} />
              <span className={cn(
                health === "ok" ? "text-success" : "text-destructive",
              )}>
                API {health === "ok" ? "Online" : health}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-2 h-2 rounded-full",
                llmReady ? "bg-info" : "bg-muted-foreground",
              )} />
              <span className={cn(
                llmReady ? "text-info" : "text-muted-foreground",
              )}>
                LLM {llmReady ? "On" : "Off"}
              </span>
            </div>
          </div>

          {isAdmin && (
            <div className="px-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md
                                text-[10px] font-medium uppercase tracking-wide
                                bg-warning/10 text-warning border border-warning/20">
                Admin
              </span>
            </div>
          )}

          {/* User chip + theme toggle (the gear-shaped slot from the
              design becomes our theme cycle button -- saves a row). */}
          <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-muted/50">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-chart-1 to-chart-3
                            flex items-center justify-center shrink-0">
              <span className="text-xs font-medium text-white">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate"
                 title={user?.email ?? ""}>{userLabel}</p>
            </div>
            <ThemeToggle />
          </div>

          <button onClick={handleSignOut}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm
                             text-muted-foreground hover:text-foreground
                             hover:bg-muted rounded-lg transition-colors">
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto flex flex-col">
        <DiagnosticsBanner />
        <div className="flex-1">
          {/* A page-level render crash shows a recoverable card here
              instead of unmounting the whole app (black screen). Keyed
              by pathname so navigating away auto-clears the error and the
              sidebar/shell stays alive throughout. */}
          <ErrorBoundary resetKey={loc.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
